import { useEffect, useId, useRef } from 'react';
import { escapeIsClaimed } from './escapeClaim';
import { modalStack } from './modalStack';

const FOCUSABLE = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

// Stack of currently-open modals (topmost last). Escape and the Tab focus-trap
// only act for the topmost, so one Escape closes exactly one dialog rather than
// every stacked modal at once. Body-scroll-lock is ref-counted off this too.
// (The array itself lives in modalStack.js; see there.)
let savedBodyOverflow = '';

// Shared dialog primitive: backdrop click + Escape to close, a focus trap while
// open, focus restored to whatever triggered it on close, and the page body
// locked from scrolling behind it. Every overlay in the app should render its
// content through this instead of hand-rolling `fixed inset-0` + its own
// key/focus handling.
//
// dismissOnBackdrop (default true): when false, clicking the backdrop does NOT
// close — used e.g. for a prompt with unsaved text, so an accidental outside
// click can't silently discard it (Escape still closes deliberately).
//
// `align` is HORIZONTAL (justify-*, center/left/right); `valign` is vertical
// (align-items, center/start/stretch). Both emit exactly one class from one
// template slot, which matters more than it looks: a caller can't reliably
// override an align-items class through `className`, because two Tailwind
// utilities in the same category are resolved by their order in the
// generated stylesheet, not by their order in the class attribute.
//
// `valign` defaults to CENTER, which it did not always. The outer overlay
// used to set no `align-items` at all, so every panel got flexbox's stretch
// default - a dialog shorter than the viewport was rendered full-height and
// top-aligned rather than sized to its content and centered (confirmed via
// computed styles). Three callers had already worked around it locally with
// `self-center`; the rest silently wore it, most visibly the small
// confirm-shaped ones with no `max-h` at all (`ConfirmContext.jsx`,
// `builder/AppOverview.jsx`, `builder/DeleteAppModal.jsx`), which stretched
// edge to edge. Fixing it at the source was deferred once for fear of
// touching every modal at once; measured rather than estimated, the real
// blast radius was ONE caller (`live/GlobalSearch.jsx`, a spotlight-style
// search that wants top alignment and now says so with `valign="start"`).
// Everything else either already opted out with `self-center`, pins its own
// height with `h-full` (the two drawers), or is strictly improved.
//
// `self-center` on a panelClassName still wins over any of this, so the
// three existing workarounds stay correct and redundant rather than wrong.
export function Modal({
  onClose, labelledBy, children, className = '', panelClassName = '', panelStyle,
  initialFocusRef, align = 'center', valign = 'center', dismissOnBackdrop = true,
}) {
  const panelRef = useRef(null);
  const triggerRef = useRef(document.activeElement);
  const id = useId();

  // Track open-modal stack + lock body scroll while any modal is open.
  useEffect(() => {
    modalStack.push(id);
    if (modalStack.length === 1) {
      savedBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    return () => {
      const i = modalStack.indexOf(id);
      if (i !== -1) modalStack.splice(i, 1);
      if (modalStack.length === 0) document.body.style.overflow = savedBodyOverflow;
    };
  }, [id]);

  useEffect(() => {
    const toFocus = initialFocusRef?.current || panelRef.current?.querySelector(FOCUSABLE) || panelRef.current;
    toFocus?.focus();
    const trigger = triggerRef.current;
    return () => { trigger?.focus?.(); };
  }, []); // eslint-disable-line

  useEffect(() => {
    const onKeyDown = (e) => {
      // Only the topmost modal reacts, so a single Escape closes one dialog,
      // not the whole stack, and the outer modal's trap doesn't fight the inner.
      if (modalStack[modalStack.length - 1] !== id) return;
      if (e.key === 'Escape') {
        // An open popover inside this dialog gets first refusal
        // (shared/escapeClaim.js). Returning without stopping is the point:
        // the event carries on to that control's own handler.
        if (escapeIsClaimed()) return;
        e.stopPropagation();
        e.stopImmediatePropagation?.();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const nodes = Array.from(panelRef.current.querySelectorAll(FOCUSABLE)).filter((n) => n.getClientRects().length > 0);
      if (!nodes.length) return;
      const first = nodes[0], last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onClose, id]);

  const justify = align === 'right' ? 'justify-end' : align === 'left' ? 'justify-start' : 'justify-center';
  const items = valign === 'stretch' ? 'items-stretch' : valign === 'start' ? 'items-start' : 'items-center';

  return (
    <div className={`fixed inset-0 z-50 flex ${justify} ${items} bg-black/25 dark:bg-black/50 ${className}`} onClick={dismissOnBackdrop ? onClose : undefined}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className={panelClassName}
        style={panelStyle}
      >
        {children}
      </div>
    </div>
  );
}
